import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/marketing/components/ui/button';
import { Input } from '@/marketing/components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/marketing/components/ui/dialog';
import { cn } from '@/marketing/lib/utils';
import {
  MAX_VOUCHER_BATCH_SIZE,
  type GenerateVouchersResult,
  type ImportVouchersResult,
  type VoucherCandidate,
} from '@/marketing/models/voucher';
import { VOUCHER_KEYS } from '@/marketing/infrastructure/influencer/voucher/utils/keys';
import { VOUCHER_MUTATIONS } from '@/marketing/infrastructure/influencer/voucher/utils/queries';
import { extractErrorMessage } from '@/marketing/lib/errorMessage';
import { buildImportRowsFromCandidates, parseVoucherUserIds } from '@/marketing/lib/voucherTargets';
import { useAuthStore } from '@/marketing/stores/useAuthStore';
import { InfluencerPicker } from './InfluencerPicker';
import { VoucherBatchResultView, type VoucherBatchCount } from './VoucherBatchResultView';

type TargetMode = 'creators' | 'userIds';

const DEFAULT_DISCOUNT = '5';
const DEFAULT_COMMISSION = '10';

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-foreground/80">
        {label}
        {required && <span className="tw-text-red-500"> *</span>}
        {hint && <span className="tw-ml-1 tw-font-normal tw-text-muted-foreground">· {hint}</span>}
      </label>
      {children}
    </div>
  );
}

function PercentInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="tw-relative">
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/[^0-9]/g, ''))}
        inputMode="numeric"
        className="tw-pr-7"
      />
      <span className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-sm tw-text-muted-foreground">
        %
      </span>
    </div>
  );
}

function ModeTab({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'tw-rounded-md tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold',
        active ? 'tw-bg-background tw-text-foreground tw-shadow-sm' : 'tw-text-muted-foreground',
      )}
    >
      {label}
    </button>
  );
}

function toGenerateCounts(result: GenerateVouchersResult): VoucherBatchCount[] {
  return [
    { label: 'Generated', value: result.generated, hint: 'new codes issued' },
    { label: 'Had a code', value: result.alreadyHadCode, hint: 'left unchanged' },
    { label: 'Skipped', value: result.skipped, hint: 'no connected social' },
    { label: 'Errors', value: result.errors.length, hint: 'listed below' },
  ];
}

function toImportCounts(result: ImportVouchersResult): VoucherBatchCount[] {
  return [
    { label: 'Issued', value: result.imported, hint: 'attached to a creator' },
    { label: 'Unclaimed', value: result.unclaimed, hint: 'no Creator Hub account yet' },
    { label: 'Skipped', value: result.skipped, hint: 'already had a code' },
    { label: 'Errors', value: result.errors.length, hint: 'listed below' },
  ];
}

interface BatchOutcome {
  counts: VoucherBatchCount[];
  errors: string[];
}

interface GenerateVouchersDialogProps {
  open: boolean;
  onClose: () => void;
}

export function GenerateVouchersDialog({ open, onClose }: GenerateVouchersDialogProps) {
  const queryClient = useQueryClient();
  const adminEmail = useAuthStore((state) => state.user)?.email ?? 'unknown';
  const [mode, setMode] = useState<TargetMode>('creators');
  const [selected, setSelected] = useState<VoucherCandidate[]>([]);
  const [userIdsText, setUserIdsText] = useState('');
  const [discount, setDiscount] = useState(DEFAULT_DISCOUNT);
  const [commission, setCommission] = useState(DEFAULT_COMMISSION);
  const [outcome, setOutcome] = useState<BatchOutcome | null>(null);

  const discountPercent = Number(discount);
  const commissionPercent = Number(commission);
  const { userIds, invalidEntries } = parseVoucherUserIds(userIdsText);
  const targetCount = mode === 'creators' ? selected.length : userIds.length;

  const settleBatch = async (counts: VoucherBatchCount[], errors: string[], summary: string) => {
    await queryClient.invalidateQueries({ queryKey: VOUCHER_KEYS.all });
    setOutcome({ counts, errors });
    toast.success(summary);
  };

  const reportFailure = (error: unknown) => {
    toast.error(extractErrorMessage(error, 'Failed to issue vouchers'));
  };

  const importMutation = useMutation({
    ...VOUCHER_MUTATIONS.import(),
    onSuccess: (result) =>
      settleBatch(
        toImportCounts(result),
        result.errors,
        `Issued ${result.imported + result.unclaimed} code(s) for ${selected.length} creator(s)`,
      ),
    onError: reportFailure,
  });

  const generateMutation = useMutation({
    ...VOUCHER_MUTATIONS.generate(),
    onSuccess: (result) =>
      settleBatch(toGenerateCounts(result), result.errors, `Generated ${result.generated} code(s)`),
    onError: reportFailure,
  });

  const isPending = importMutation.isPending || generateMutation.isPending;
  const canSubmit =
    targetCount > 0 &&
    targetCount <= MAX_VOUCHER_BATCH_SIZE &&
    invalidEntries.length === 0 &&
    discountPercent >= 1 &&
    discountPercent <= 100 &&
    commissionPercent >= 0 &&
    commissionPercent <= 100 &&
    !isPending;

  const close = () => {
    setSelected([]);
    setUserIdsText('');
    setOutcome(null);
    onClose();
  };

  const submit = () => {
    if (mode === 'creators') {
      importMutation.mutate({
        rows: buildImportRowsFromCandidates({ candidates: selected, commissionPercent }),
        importedBy: adminEmail,
        discountPercent,
      });
      return;
    }

    generateMutation.mutate({
      targets: userIds.map((userId) => ({ userId, commissionPercent })),
      discountPercent,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && close()}>
      <DialogContent className="tw-max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Issue affiliate codes</DialogTitle>
        </DialogHeader>

        {outcome ? (
          <VoucherBatchResultView counts={outcome.counts} errors={outcome.errors} />
        ) : (
          <div className="tw-space-y-4">
            <div className="tw-inline-flex tw-gap-1 tw-rounded-lg tw-bg-muted tw-p-1">
              <ModeTab active={mode === 'creators'} label="Search creators" onClick={() => setMode('creators')} />
              <ModeTab active={mode === 'userIds'} label="WP user IDs" onClick={() => setMode('userIds')} />
            </div>

            {mode === 'creators' ? (
              <Field label="Creators" required hint={`search and select, max ${MAX_VOUCHER_BATCH_SIZE}`}>
                <InfluencerPicker selected={selected} onChange={setSelected} />
              </Field>
            ) : (
              <Field label="WP user IDs" required hint="comma, space or newline separated">
                <textarea
                  value={userIdsText}
                  onChange={(event) => setUserIdsText(event.target.value)}
                  rows={4}
                  placeholder="18201, 18202, 18203"
                  className="tw-w-full tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm"
                />
                {invalidEntries.length > 0 && (
                  <p className="tw-mt-1 tw-text-xs tw-text-red-600">Not user IDs: {invalidEntries.join(', ')}</p>
                )}
              </Field>
            )}

            <div className="tw-grid tw-grid-cols-2 tw-gap-3">
              <Field label="Discount" required hint="buyer discount, whole batch">
                <PercentInput value={discount} onChange={setDiscount} />
              </Field>
              <Field label="Commission" required hint="applied to every target">
                <PercentInput value={commission} onChange={setCommission} />
              </Field>
            </div>

            <div className="tw-rounded-lg tw-border tw-border-border tw-bg-muted/40 tw-px-3.5 tw-py-2.5 tw-text-xs tw-text-muted-foreground">
              {mode === 'creators' ? (
                <p>
                  Codes are issued by handle. A creator who has not joined Creator Hub still gets a code, held unclaimed
                  until they connect that handle.
                </p>
              ) : (
                <p>
                  Codes are issued against the connected social account of each Creator Hub user. A user with no
                  connected account is skipped and reported below.
                </p>
              )}
              <p className="tw-mt-1">
                The backend owns code generation and leaves an existing code untouched, so re-running is safe.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {outcome ? (
            <Button onClick={close}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button onClick={submit} disabled={!canSubmit}>
                {isPending ? 'Issuing…' : `Issue ${targetCount} code${targetCount === 1 ? '' : 's'}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
